/**
 * User Notification Service
 * Handles API requests related to user notifications
 */
import { createService } from './serviceFactory';

// Define the base URL for user notification service
const baseUrl = '/api/supplier/user-notification';

// Create base service
const baseService = createService(baseUrl);

const userNotificationService = {
  normalizeTableName(value) {
    return (value || '').toString().trim().toLowerCase();
  },

  resolveByTableName(tableName, entityId) {
    if (tableName === 'purchase-order-message-to-buyer') {
      return { path: `/purchase-order/${entityId}`, query: { tab: 'message-to-buyer' } };
    }
    if (tableName === 'purchase-orders') {
      return { path: `/purchase-order/${entityId}`, query: { tab: 'comments' } };
    }
    if (tableName === 'purchaseorder' || tableName === 'purchaseorders') {
      return { path: `/purchase-order/${entityId}`, query: {} };
    }
    if (tableName === 'quotation' || tableName === 'quotations') {
      return { path: `/rfq/${entityId}`, query: {} };
    }
    return null;
  },

  resolveByEntityType(relatedEntityType, entityId) {
    if (relatedEntityType === 'purchaseorder') {
      return { path: `/purchase-order/${entityId}`, query: {} };
    }
    if (relatedEntityType === 'quotation' || relatedEntityType === 'rfq') {
      return { path: `/rfq/${entityId}`, query: {} };
    }
    // Sent to super admins when a supplier submits for verification; entityId is the supplier tenant.
    if (relatedEntityType === 'supplierverification') {
      return { path: `/admin/tenants/${entityId}`, query: { tab: 'registration' } };
    }
    // Sent to the supplier after a reviewer's decision; entityId is their own tenant.
    if (relatedEntityType === 'supplieronboarding') {
      return { path: '/onboarding', query: {} };
    }
    if (relatedEntityType === 'supplierverified') {
      return { path: '/profile', query: {} };
    }
    return null;
  },

  resolveNavigationTarget(notification) {
    const data = notification?.data || notification || {};
    const payload = data.payload || {};
    const tableName = this.normalizeTableName(data.tableName || payload.tableName);
    const sourceReferenceId = data.sourceReferenceId || payload.rowId || null;
    const entityId = data.entityId || payload.entityId || payload.nozzleId || sourceReferenceId;

    if (!entityId) return null;

    const byTable = this.resolveByTableName(tableName, entityId);
    if (byTable) return byTable;

    const relatedEntityType = this.normalizeTableName(data.relatedEntityType || notification?.relatedEntityType);
    return this.resolveByEntityType(relatedEntityType, entityId);
  },

  /**
   * Get paginated list of user notifications (my-notifications endpoint)
   * @param {Object} params - Query parameters
   * @returns {Promise} - Response with notification items
   */
  async getMyNotifications(params = {}) {
    const queryParams = new URLSearchParams();
    
    // Add supported query parameters
    if (params.userId) queryParams.append('UserId', params.userId);
    if (params.notificationType) queryParams.append('NotificationType', params.notificationType);
    if (params.isRead !== undefined) queryParams.append('IsRead', params.isRead);
    if (params.startDate) queryParams.append('StartDate', params.startDate);
    if (params.endDate) queryParams.append('EndDate', params.endDate);
    if (params.pageSize) queryParams.append('PageSize', params.pageSize);
    if (params.skipCount) queryParams.append('SkipCount', params.skipCount);
    if (params.sorting) queryParams.append('Sorting', params.sorting);

    const url = `${baseUrl}/my-notifications${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    const response = await baseService.client.get(url);
    // Return the items array from the response
    return response.data.items || response.data;
  },

  /**
   * Get recent notifications (limited count)
   * @param {number} maxCount - Maximum number of notifications to return
   * @returns {Promise} - Response with recent notifications
   */
  async getRecentNotifications(maxCount = 5) {
    const response = await baseService.client.get(`${baseUrl}/recent-notifications?maxCount=${maxCount}`);
    // Return the items array from the response, or the response itself if it's already an array
    return response.data.items || response.data;
  },

  /**
   * Get unread count for a user
   * @param {string} userId - User ID
   * @returns {Promise} - Response with unread count
   */
  async getUnreadCount(userId) {
    const response = await baseService.client.get(`${baseUrl}/unread-count/${userId}`);
    return response.data;
  },

  /**
   * Get a single notification by ID
   * @param {string} id - Notification ID
   * @returns {Promise} - Response with notification details
   */
  async getById(id) {
    const response = await baseService.client.get(`${baseUrl}/${id}`);
    return response.data;
  },

  /**
   * Create a new notification
   * @param {Object} notificationData - Notification data
   * @returns {Promise} - Response with created notification
   */
  async create(notificationData) {
    const response = await baseService.client.post(baseUrl, notificationData);
    return response.data;
  },

  /**
   * Delete a notification
   * @param {string} id - Notification ID
   * @returns {Promise} - Response
   */
  async delete(id) {
    const response = await baseService.client.delete(`${baseUrl}/${id}`);
    return response.data;
  },

  /**
   * Mark specific notifications as read
   * @param {Array} notificationIds - Array of notification IDs
   * @returns {Promise} - Response
   */
  async markAsRead(notificationIds) {
    const data = {
      notificationIds: Array.isArray(notificationIds) ? notificationIds : [notificationIds]
    };
    const response = await baseService.client.post(`${baseUrl}/mark-as-read`, data);
    return response.data;
  },

  /**
   * Mark all notifications as read for a user
   * @param {string} userId - User ID
   * @returns {Promise} - Response
   */
  async markAllAsRead(userId) {
    const data = { userId };
    const response = await baseService.client.post(`${baseUrl}/mark-all-as-read`, data);
    return response.data;
  },

  /**
   * Update notification status
   * @param {string} id - Notification ID
   * @param {Object} statusData - Status update data
   * @returns {Promise} - Response
   */
  async updateStatus(id, statusData) {
    const response = await baseService.client.put(`${baseUrl}/${id}/status`, statusData);
    return response.data;
  },

  /**
   * Cleanup old notifications
   * @param {number} daysOld - Number of days old to cleanup
   * @returns {Promise} - Response
   */
  async cleanupOldNotifications(daysOld = 30) {
    const response = await baseService.client.post(`${baseUrl}/cleanup-old-notifications?daysOld=${daysOld}`);
    return response.data;
  },

  /**
   * Transform API notification to UI format
   * @param {Object} apiNotification - Notification from API
   * @returns {Object} - Transformed notification for UI
   */
  transformNotification(apiNotification) {
    if (!apiNotification) return null;

    // Parse payload if it exists and is a string
    let parsedPayload = null;
    if (apiNotification.payload) {
      try {
        parsedPayload = typeof apiNotification.payload === 'string' 
          ? JSON.parse(apiNotification.payload) 
          : apiNotification.payload;
      } catch (error) {
        console.warn('Failed to parse notification payload:', error);
      }
    }

    // Use entityId from payload if available, otherwise use relatedEntityId
    let entityId = apiNotification.relatedEntityId;
    if (parsedPayload && parsedPayload.entityId) {
      entityId = parsedPayload.entityId;
    }

    // Use rowId from payload as fallback for entityId if needed
    if (!entityId && parsedPayload && parsedPayload.rowId) {
      entityId = parsedPayload.rowId;
    }

    return {
      id: apiNotification.id,
      type: this.mapNotificationType(apiNotification.notificationType),
      title: apiNotification.title,
      message: apiNotification.content,
      createdAt: apiNotification.creationTime || apiNotification.sentAt,
      isRead: apiNotification.isRead,
      readAt: apiNotification.readAt,
      priority: apiNotification.priority,
      status: apiNotification.status,
      data: {
        tableName: parsedPayload?.tableName || apiNotification.relatedEntityType,
        entityId: entityId,
        sourceReferenceId: apiNotification.sourceReferenceId,
        relatedEntityType: apiNotification.relatedEntityType,
        payload: parsedPayload,
        channelType: apiNotification.channelType,
        externalNotificationId: apiNotification.externalNotificationId
      }
    };
  },

  /**
   * Map API notification type to UI notification type
   * @param {string} apiType - API notification type
   * @returns {string} - UI notification type
   */
  mapNotificationType(apiType) {
    if (!apiType) return 'system';
    
    const typeMapping = {
      'comment': 'comment',
      'quotation': 'quotation',
      'rfq': 'quotation',
      'system': 'system',
      'maintenance': 'system',
      'update': 'system'
    };
    
    const lowerType = apiType.toLowerCase();
    return typeMapping[lowerType] || 'system';
  },

  /**
   * Get notification icon based on type
   * @param {string} type - Notification type
   * @returns {string} - Icon class
   */
  getNotificationIcon(type) {
    const iconMap = {
      comment: 'ti ti-message-circle',
      quotation: 'ti ti-file-text',
      system: 'ti ti-settings',
      default: 'ti ti-bell'
    };
    return iconMap[type] || iconMap.default;
  },

  /**
   * Get notification color class based on type
   * @param {string} type - Notification type
   * @returns {string} - Color class
   */
  getNotificationColorClass(type) {
    const colorMap = {
      comment: 'text-primary',
      quotation: 'text-info',
      system: 'text-warning',
      default: 'text-secondary'
    };
    return colorMap[type] || colorMap.default;
  }
};

export default userNotificationService;
